import { autoUpdate, computePosition } from "@floating-ui/dom";
import { onScopeDispose } from "vue";

/**
 * Keeps a floating element positioned relative to a reference element.
 *
 * Wraps floating-ui's `computePosition` and `autoUpdate`: `autoPosition` resolves
 * the position immediately and re-applies the `translate(x, y)` transform on every
 * scroll/resize until `stopAutoPosition` is called or the owning component unmounts.
 *
 * @returns {{ autoPosition: Function, stopAutoPosition: Function }}
 *   `autoPosition(referenceEl, floatingEl, options, onPositioned)` — positions the
 *   element and keeps it updated; `options` are `computePosition` options
 *   (placement, middleware). `onPositioned` is called with the resolved
 *   `{ x, y, placement, ... }` after each positioning.
 *   `stopAutoPosition()` — cancels the auto-update loop; safe to call repeatedly.
 *
 * @example
 * const { autoPosition, stopAutoPosition } = useFloatingUi();
 *
 * const show = () => {
 *   visible.value = true;
 *   nextTick(() =>
 *     autoPosition(activatorEl.value, menuEl.value, {
 *       placement: "bottom-start",
 *       middleware: [offset(6), flip()],
 *     }),
 *   );
 * };
 * const hide = () => {
 *   stopAutoPosition();
 *   visible.value = false;
 * };
 */
export const useFloatingUi = () => {
  let stopAutoUpdate = null;

  const autoPosition = (referenceEl, floatingEl, options, onPositioned) => {
    stopAutoPosition();
    if (!referenceEl || !floatingEl) return;
    stopAutoUpdate = autoUpdate(
      referenceEl,
      floatingEl,
      () =>
        computePosition(referenceEl, floatingEl, options).then(({ x, y, ...rest }) => {
          Object.assign(floatingEl.style, { transform: `translate(${x}px, ${y}px)` });
          onPositioned?.({ x, y, ...rest });
        }),
      { ancestorScroll: true, ancestorResize: true, elementResize: true },
    );
  };

  function stopAutoPosition() {
    stopAutoUpdate?.();
    stopAutoUpdate = null;
  }

  onScopeDispose(stopAutoPosition);

  return { autoPosition, stopAutoPosition };
};
